import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, Button, Collapse, Dialog, DialogContent, Stack, Typography } from '@mui/material';
import {
  CheckCircleOutline,
  ErrorOutline,
  ExpandMore,
  LocalFireDepartment,
  Restaurant,
  Schedule,
  DirectionsRun
} from '@mui/icons-material';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';
import RestAdviceNote from 'components/play/RestAdviceNote';
import { restSkipSummary } from 'components/play/restAdvice';
import { formatDuration, repsLabelOf, secondsDoneTotal, secondsLabelOf } from 'components/play/workoutEstimate';

function StatPill({ icon, value, label }) {
  return (
    <Stack
      direction="row"
      spacing={0.75}
      alignItems="center"
      sx={{
        px: 1.5,
        py: 0.75,
        borderRadius: 999,
        border: '1px solid',
        borderColor: 'hairline.main',
        backgroundColor: 'surface.overlay'
      }}>
      {icon}
      <Typography variant="caption" fontWeight={800} sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
    </Stack>
  );
}

StatPill.propTypes = {
  icon: PropTypes.node.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  label: PropTypes.string.isRequired
};

/**
 * Un ejercicio del cierre, plegable: cerrado resume lo hecho y abierto muestra serie por serie.
 * Una rutina trae 6-8 ejercicios y cada uno 3-4 series: en plano serían 30 filas, así que el
 * detalle solo se monta cuando la persona lo pide.
 */
function ExerciseBreakdown({ item }) {
  const { t } = useTranslation(['play']);
  const [open, setOpen] = useState(false);

  const setsDone = item.setsDone || 0;
  // Los medidos por TIEMPO no tienen reps que mostrar: su marca es el cronómetro de cada serie.
  const isTimed = item.metric === 'TIME_SECONDS';
  const doneLabel = isTimed ? secondsLabelOf(item) : repsLabelOf(item);
  const lastRestIndex = (item.sets || 1) - 1;
  const expandable = setsDone > 0;

  const toggle = () => {
    if (expandable) setOpen((o) => !o);
  };

  return (
    <Box
      sx={{
        borderRadius: '12px',
        border: '1px solid',
        borderColor: 'hairline.main',
        backgroundColor: 'surface.raised',
        overflow: 'hidden',
        transition: 'border-color 220ms cubic-bezier(0.32,0.72,0,1)',
        '&:hover': { borderColor: expandable ? 'hairline.strong' : 'hairline.main' }
      }}>
      <Stack
        direction="row"
        spacing={1.25}
        alignItems="center"
        role={expandable ? 'button' : undefined}
        tabIndex={expandable ? 0 : undefined}
        aria-expanded={expandable ? open : undefined}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
        sx={{
          px: 1.5,
          py: 1.25,
          cursor: expandable ? 'pointer' : 'default',
          transition: 'transform 200ms cubic-bezier(0.32,0.72,0,1)',
          '&:active': { transform: expandable ? 'scale(0.98)' : 'none' }
        }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" fontWeight={800} noWrap>
            {item.name}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }} noWrap>
            {t('perf.workoutSets', { ns: 'play', done: setsDone, total: item.sets || 0 })}
            {doneLabel ? ` · ${doneLabel}${isTimed ? ' s' : ` ${t('card.reps', { ns: 'play' })}`}` : ''}
          </Typography>
        </Box>
        {secondsDoneTotal(item) > 0 && (
          <Typography variant="caption" fontWeight={800} sx={{ flex: 'none', fontVariantNumeric: 'tabular-nums' }}>
            {formatDuration(secondsDoneTotal(item))}
          </Typography>
        )}
        {expandable && (
          <ExpandMore
            sx={{
              flex: 'none',
              fontSize: 20,
              color: 'text.secondary',
              transform: open ? 'rotate(180deg)' : 'none',
              transition: 'transform 220ms cubic-bezier(0.32,0.72,0,1)'
            }}
          />
        )}
      </Stack>

      <Collapse in={open} unmountOnExit>
        <Stack spacing={0.75} sx={{ px: 1.5, pb: 1.5 }}>
          {Array.from({ length: setsDone }, (_, i) => {
            const seconds = Number(item.secondsDone?.[i]) || 0;
            const reps = Number(item.repsDone?.[i]) || 0;
            const weight = Number(item.weights?.[i]) || 0;
            const rest = item.restDone?.[i];
            // `null` = descanso que no llegó a ocurrir (se salió de la sesión ahí); no es 0 s.
            const hasRest = rest !== null && rest !== undefined && Number.isFinite(Number(rest));
            const restShort = Boolean(item.restSkipped?.[i]);
            return (
              <Stack
                key={i}
                direction="row"
                spacing={1}
                alignItems="baseline"
                sx={{ pt: 0.75, borderTop: '1px solid', borderColor: 'hairline.main', flexWrap: 'wrap' }}
                useFlexGap>
                <Typography variant="caption" sx={{ color: 'text.secondary', flex: 'none', minWidth: 56 }}>
                  {t('workoutSummary.setNumber', { ns: 'play', number: i + 1, defaultValue: 'Serie {{number}}' })}
                </Typography>
                <Typography variant="caption" fontWeight={800} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {isTimed || reps === 0
                    ? t('workoutSummary.setSeconds', { ns: 'play', seconds, defaultValue: '{{seconds}} s' })
                    : t('workout.reps', { ns: 'play', count: reps })}
                </Typography>
                {!isTimed && seconds > 0 && (
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                    · {t('workoutSummary.setSeconds', { ns: 'play', seconds, defaultValue: '{{seconds}} s' })}
                  </Typography>
                )}
                {weight > 0 && (
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                    · {t('workoutSummary.setWeight', { ns: 'play', weight, defaultValue: '{{weight}} kg' })}
                  </Typography>
                )}
                {/* El descanso de la ÚLTIMA serie es el cambio de ejercicio, no un descanso del objetivo. */}
                {hasRest && i < lastRestIndex && (
                  <Typography
                    variant="caption"
                    sx={{
                      ml: 'auto',
                      fontVariantNumeric: 'tabular-nums',
                      color: restShort ? 'sessionStatus.pending.fg' : 'text.secondary'
                    }}>
                    {t('workoutSummary.setRest', { ns: 'play', seconds: Number(rest), defaultValue: 'descanso {{seconds}} s' })}
                  </Typography>
                )}
              </Stack>
            );
          })}
        </Stack>
      </Collapse>
    </Box>
  );
}

ExerciseBreakdown.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    metric: PropTypes.string,
    sets: PropTypes.number,
    setsDone: PropTypes.number,
    reps: PropTypes.string,
    repsDone: PropTypes.array,
    secondsDone: PropTypes.array,
    weights: PropTypes.array,
    restDone: PropTypes.array,
    restSkipped: PropTypes.array
  }).isRequired
};

/**
 * OTRIX Play — cierre del entrenamiento: completado (verde de sesiones) o incompleto
 * (ámbar warning) con % de éxito real, tiempo, series y kcal. Recuerda que la comida
 * también es parte del progreso, con acceso directo al plan nutricional.
 */
function PlayWorkoutSummaryModal({ open, summary = null, onViewPerformance, onViewNutrition }) {
  const { t } = useTranslation(['play']);

  if (!summary) return null;

  const { completed, percent, totalSeconds, doneSets, totalSets, calories, exercises } = summary;
  const trained = (exercises || []).filter((ex) => (ex.setsDone || 0) > 0);
  // Aviso ACSM del cierre: solo aparece si de verdad se cortaron descansos entre series.
  const restAdvice = restSkipSummary(t, exercises);

  return (
    <Dialog
      open={open}
      onClose={onViewPerformance}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          backgroundColor: 'surface.content',
          backgroundImage: 'none',
          border: '1px solid',
          borderColor: 'hairline.main'
        }
      }}>
      <DialogContent>
        <Stack spacing={2.5} alignItems="center" sx={{ py: 2, textAlign: 'center' }}>
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              flex: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: completed ? 'sessionStatus.completed.bg' : 'sessionStatus.pending.bg',
              color: completed ? 'sessionStatus.completed.fg' : 'sessionStatus.pending.fg'
            }}>
            {completed ? <CheckCircleOutline sx={{ fontSize: 40 }} /> : <ErrorOutline sx={{ fontSize: 40 }} />}
          </Box>

          <Stack spacing={0.5} alignItems="center">
            <Typography variant="h5" fontWeight={800} letterSpacing="-0.01em">
              {completed
                ? t('workoutSummary.completedTitle', { ns: 'play', defaultValue: 'Entrenamiento completado' })
                : t('workoutSummary.partialTitle', { ns: 'play', defaultValue: 'Entrenamiento guardado' })}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 320 }}>
              {completed
                ? t('workoutSummary.completedBody', { ns: 'play', defaultValue: 'Cumpliste todas las series del día. Disciplina hoy, resultados mañana.' })
                : t('workoutSummary.partialBody', {
                    ns: 'play',
                    percent,
                    defaultValue: 'Llegaste al {{percent}}% de las series. Quedó guardado: cada serie cuenta.'
                  })}
            </Typography>
          </Stack>

          {/* % de éxito grande */}
          <Typography
            variant="h2"
            fontWeight={800}
            sx={{
              lineHeight: 1,
              fontVariantNumeric: 'tabular-nums',
              color: completed ? 'sessionStatus.completed.fg' : 'sessionStatus.pending.fg'
            }}>
            {percent}%
          </Typography>

          <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
            <StatPill
              icon={<Schedule sx={{ fontSize: 14, color: 'gold.main' }} />}
              value={formatDuration(totalSeconds)}
              label={t('workoutSummary.time', { ns: 'play', defaultValue: 'tiempo' })}
            />
            <StatPill
              icon={<DirectionsRun sx={{ fontSize: 14, color: 'gold.main' }} />}
              value={`${doneSets}/${totalSets}`}
              label={t('workoutSummary.sets', { ns: 'play', defaultValue: 'series' })}
            />
            {calories > 0 && (
              <StatPill
                icon={<LocalFireDepartment sx={{ fontSize: 14, color: 'gold.main' }} />}
                value={calories}
                label="kcal"
              />
            )}
          </Stack>

          {/* Qué hiciste ejercicio por ejercicio (serie a serie al abrir cada uno) */}
          {trained.length > 0 && (
            <Box sx={{ width: '100%', textAlign: 'left' }}>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', mb: 1 }}>
                {t('workoutSummary.breakdownTitle', { ns: 'play', defaultValue: 'Lo que hiciste' })}
              </Typography>
              <Stack spacing={1}>
                {trained.map((ex) => (
                  <ExerciseBreakdown key={ex.id || ex.name} item={ex} />
                ))}
              </Stack>
            </Box>
          )}

          {restAdvice && <RestAdviceNote advice={restAdvice} />}

          {/* La comida también entrena */}
          <Box
            sx={{
              width: '100%',
              p: 1.75,
              borderRadius: '12px',
              border: '1px solid',
              borderColor: 'hairline.main',
              backgroundColor: 'surface.overlay',
              textAlign: 'left'
            }}>
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Restaurant sx={{ color: 'gold.main', flex: 'none' }} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="caption" fontWeight={800} display="block">
                  {t('workoutSummary.nutritionTitle', { ns: 'play', defaultValue: 'La comida también es parte del plan' })}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {t('workoutSummary.nutritionBody', { ns: 'play', defaultValue: 'Entrenar sin comer bien es remar contra la corriente.' })}
                </Typography>
              </Box>
              <Button
                size="small"
                onClick={onViewNutrition}
                sx={{ flex: 'none', color: 'gold.main', fontWeight: 800, textTransform: 'none' }}>
                {t('workoutSummary.nutritionCta', { ns: 'play', defaultValue: 'Ver nutrición' })}
              </Button>
            </Stack>
          </Box>

          <Button fullWidth onClick={onViewPerformance} sx={{ ...premiumButtonSx, py: 1.2 }}>
            {t('workoutSummary.viewPerformance', { ns: 'play', defaultValue: 'Ver mi rendimiento' })}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

PlayWorkoutSummaryModal.propTypes = {
  open: PropTypes.bool.isRequired,
  summary: PropTypes.shape({
    completed: PropTypes.bool,
    percent: PropTypes.number,
    totalSeconds: PropTypes.number,
    doneSets: PropTypes.number,
    totalSets: PropTypes.number,
    calories: PropTypes.number,
    /** Plan del runner ya copiado (el desglose por ejercicio y el aviso de descansos). */
    exercises: PropTypes.array
  }),
  onViewPerformance: PropTypes.func.isRequired,
  onViewNutrition: PropTypes.func.isRequired
};

export default PlayWorkoutSummaryModal;
